import type { SelectOption } from "@p0security/shared/types/lister";
import { z } from "zod";

import { DATABRICKS_INTEGRATION_KEY } from "./constants.ts";

/**
 * The Databricks install API's routes, under `integrations/databricks`: each
 * an Express `pattern` for the router, and the `path` the frontend fetches.
 */
export const DatabricksApiRoutes = {
  /** The catalogs an installed workspace's principal can see. */
  catalogs: {
    pattern: "/workspace/:workspaceId/catalogs",
    path: (workspaceId: string) =>
      `integrations/${DATABRICKS_INTEGRATION_KEY}/workspace/${encodeURIComponent(workspaceId)}/catalogs`,
  },
  /**
   * The `admin` requests on an installed account or workspace that may still
   * hold its role, which turning its switch off would leave unrevokable.
   */
  heldAdminRequests: {
    pattern: "/admin-scope/:scope/held-requests",
    path: (scope: string) =>
      `integrations/${DATABRICKS_INTEGRATION_KEY}/admin-scope/${encodeURIComponent(scope)}/held-requests`,
  },
} as const;

const ListerResponse = z.object({
  ok: z.literal(true),
  items: z.array(
    z.object({ key: z.string(), value: z.string() })
  ) satisfies z.ZodType<SelectOption[]>,
});

/** What the catalogs route responds with: the connector lister's items. */
export type ListerResponse = z.infer<typeof ListerResponse>;

/** A route's response body, parsed with `schema`. Throws, saying what's wrong, if it doesn't parse. */
const parseResponse = <T>(schema: z.ZodType<T>, body: unknown): T => {
  const parsed = schema.safeParse(body);
  if (!parsed.success) {
    throw new Error(
      `P0's install API returned an unexpected response: ${z.prettifyError(parsed.error)}`
    );
  }
  return parsed.data;
};

/** The items in a catalogs response body. Throws if it isn't a {@link ListerResponse}. */
export const parseListerItems = (body: unknown): SelectOption[] =>
  parseResponse(ListerResponse, body).items;

const HeldAdminRequestsResponse = z.object({
  ok: z.literal(true),
  requestIds: z.array(z.string()),
});

/** What the held admin requests route responds with: the requests' IDs. */
export type HeldAdminRequestsResponse = z.infer<
  typeof HeldAdminRequestsResponse
>;

/** The request IDs in a held admin requests response body. Throws if it isn't a {@link HeldAdminRequestsResponse}. */
export const parseHeldAdminRequests = (body: unknown): string[] =>
  parseResponse(HeldAdminRequestsResponse, body).requestIds;
